export async function getPlatformVersion() {
  // The dynamic import below was disabled (reason predates this fix) but the
  // `PKG` read that depended on it was left in place, guaranteeing
  // `ReferenceError: PKG is not defined` on every call — GovernanceFields.js
  // calls this live on render. Found via the tickets/fable.md CI guard,
  // workspace-scope check (packages/workspace/dist bundle, 2026-08-08).
  // Restoring the import is a separate decision (it may be why it was
  // disabled in the first place — a dynamic `import()` of a bare
  // `package.json` has its own bundler considerations); until then this
  // returns undefined instead of throwing.
  // const PKG = await import('@symbo.ls/platform/package.json')
  // if (PKG) return PKG.version
  return undefined
}

// NO BUILT-IN ELEMENT METHODS HERE (WS-SHARED-METHODS-SHADOW-FRAMEWORK-ELEMENT-
// METHODS-1; Nika 2026-09-27). getRoot, getRootState, getRef, getChildren,
// getRootData, getRootContext and getContext are FRAMEWORK element methods
// (@symbo.ls/utils, bound on every element by @symbo.ls/element). This
// library shipped stale copies of all seven; since smbls 3.14.810 the framework
// refuses a library copy of a protected built-in (mergeSharedLibraries), so the
// six left after getRoot were dead code, and they are deleted. The logging
// hooks (log/warn/error/verbose, ./log.js) are the framework's documented
// override points and stay. Locked by
// packages/workspace/__tests__/sharedLibraryMethodsDoNotShadowBuiltins.test.js.
//
// Why getRoot went first (FW-MODULE-ELEMENT-GETROOT-RESOLVES-THE-MODULE-
// STORE-1). `getRoot` is a FRAMEWORK element method (@symbo.ls/utils, bound on
// every element by @symbo.ls/element): it answers `__ref.root` — the app root
// element create() stamps on every element — and falls back to the root state
// only after that. This library's `methods` are merged into the app's
// `context.methods` (mergeSharedLibraries), and the element prototype layers
// `context.methods` OVER the built-in element methods, so a copy here REPLACES
// the framework getRoot on every workspace element (Rule 63: a shared-library
// duplicate of a built-in silently shadows it). The copy that lived here read
// only `this.getRootState()?.__element`. Inside an installed module that is
// the MODULE store (workspace functions/moduleStateScope.js), which names no
// element, so getRoot() answered undefined and every module popover was dead
// (openDropdown reads getRoot().DropdownRoot) — measured on dev 2026-09-26,
// crm-app 1.0.54.
// Locked by packages/workspace/__tests__/moduleGetRootResolvesShellRoot.test.js.

export function getAppKey() {
  return this.getRootState()?.appKey
}

export function isPlatform() {
  return (
    this.getRootState('projectType') === 'platform' ||
    [
      'brand.symbo.ls',
      'smbls.symbo.ls',
      'platform.symbo.ls',
      'docs.symbo.ls',
      'assistant.symbo.ls',
      'landing.symbo.ls',
      'pricing.symbo.ls'
    ].includes(this.getAppKey())
  )
}

export function getSchema(key) {
  const { schema } = this.getRootState() || {}
  return key ? schema?.[key] : schema
}

export function getData(key) {
  const rootState = this.getRootState() || this.state?.root || {}
  const source = rootState.data || rootState
  const {
    designSystem,
    state,
    components,
    snippets,
    pages,
    functions,
    methods,
    files,
    dependencies
  } = source
  const data = {
    designSystem,
    state,
    components,
    snippets,
    pages,
    functions,
    methods,
    files,
    dependencies
  }
  return key ? data[key] : data
}

export function getMySharedLibraries() {
  const root = this.getRootState && this.getRootState()
  return (root && root.sharedLibraries) || []
}

export async function getDefaultDesignSystem() {
  return {} // await import('@symbo.ls/library/default/designSystem')
}

export async function getDefaultComponents() {
  return {} // await import('@symbo.ls/library/default/components')
}

export async function getDefaultComponentsSchema() {
  const { DEFAULT_COMPONENTS_SCHEMA } = {} // await import('@symbo.ls/library/default.js')
  return DEFAULT_COMPONENTS_SCHEMA
}

export async function getDefaultLibrary() {
  const { LIBRARY } = {} // await import('@symbo.ls/library/default.js')
  return LIBRARY
}

export async function getDefaultData(key) {
  const data = {
    designSystem: await this.getDefaultDesignSystem(),
    components: await this.getDefaultComponents()
  }
  return key ? data[key] : data
}

export function getDefaults(key) {
  const defs = this.getRootState('DEFAULTS')
  return defs?.[key] || defs
}

export function getBuiltins() {
  const defs = this.getRootState('DEFAULTS')
  return defs?.DS_DEFAULTS
}

export function getSecrets(key) {
  const secrets = this.getRootState('secrets')
  return key ? secrets?.[key] : secrets
}

export function getDependencies(key) {
  const dependencies = this.getData('dependencies')
  return key ? dependencies?.[key] : dependencies
}

export function getFiles(key) {
  const files = this.getData('files')
  return key ? files?.[key] : files
}

export function getComponents(key) {
  const components = this.getData('components')
  return key ? components?.[key] : components
}

export function getFunctions(key) {
  const functions = this.getData('functions')
  return key ? functions?.[key] : functions
}

export function getState(key) {
  const state = this.getData('state')
  return key ? state?.[key] : state
}

export function getMethods(key) {
  const methods = this.getData('methods')
  return key ? methods?.[key] : methods
}

export function getActivePkgManager() {
  return this.getRootState('pkgManager')
}

export function getPackages(key) {
  const packages = this.getSchema('dependencies')
  return key && packages ? packages[key] : packages || {}
}

export function getParsedState(obj = this) {
  const isState2 = this.isState(obj)
  if (isState2) {
    return obj.parse()
  }

  const hasState = this.isState(obj.state)
  if (hasState) {
    return obj.state.parse()
  }

  return (obj.parseDeep && obj.parseDeep()) || obj
}

export function getUtils() {
  return this.context.utils
}

export function getUtil(key) {
  return this.getUtils()[key]
}

export function getUserSettings(key) {
  const userSettings = this.getRootState('user')
  return key ? userSettings?.[key] : userSettings
}

export function setUserSettings(key, val, opts) {
  const autoSaveSettings = false //this.getUserSettings('autoSaveSettings')
  this.getRootState().update(
    {
      user: { [key]: val }
    },
    autoSaveSettings ? opts : { ...opts, preventUpdate: true }
  )
  // console.log(key, val, opts)
  this.call('setCookie', key, val)
}

export function toggleUserSettings(key, opts) {
  const val = !this.getUserSettings(key)
  this.setUserSettings(key, val, opts)
}

export function setAllUserSettings(obj, opts) {
  // console.log(obj)
  this.getRootState().update({ user: obj }, opts)

  for (const prop in obj) {
    this.call('setCookie', prop, this.getUserSettings(prop))
  }
}

export function isState(obj) {
  if (!this.call('isObjectLike', obj)) {
    return false
  }
  return (
    obj.update &&
    obj.parse &&
    obj.clean &&
    obj.create &&
    obj.parent &&
    obj.destroy &&
    obj.rootUpdate &&
    obj.parentUpdate &&
    obj.keys &&
    obj.values &&
    obj.toggle &&
    obj.replace &&
    obj.quietUpdate &&
    obj.quietReplace &&
    obj.add &&
    obj.apply &&
    obj.applyReplace &&
    obj.applyFunction &&
    obj.__element &&
    obj.__children
  )
  // return arrayContainsOtherArray(keys, ['update', 'parse', 'clean', 'create', 'parent', 'rootUpdate'])
}

export function addToPlatformContext(type, key) {
  const context = this.context
  const item = this.getDataItem(key, type)
  context[type][key] = this.call('deepClone', item)
}

export function addIfPlatformProject(type, key) {
  if (this.isPlatform()) this.addToPlatformContext(type, key)
}

export function getGlobalTheme() {
  const platformState = this.getRootState()
  if (!platformState) return 'dark'
  const { designSystem } = platformState
  return platformState.sceneTheme || designSystem?.globalTheme
}
